import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Badge, Form as BootstrapForm, InputGroup, FormControl, Modal } from "react-bootstrap";
import { BiTrash, BiBarcode, BiChevronRight, BiWrench, BiShieldQuarter, BiCheckDouble, BiGridAlt } from "react-icons/bi";
import { FaCheckCircle, FaWrench } from "react-icons/fa";
import { useFormikContext, Field } from "formik";
import { formatCurrency } from "../../utility";
import RefurbTagInput from "../warranty/components/RefurbTagInput";
import styled from "styled-components";
import { debounce } from 'lodash';
import SerializedItemsGrid from "./components/SerializedItemsGrid";
import BulkSerialGenerator from "./components/BulkSerialGenerator";
import { toast } from "react-toastify";
const CONDITION_LIST = ["Brand New", "Used Grade A", "Used Grade B", "Refurbished", "Damaged", "Used", "Other"];

const FormikSelectWrapper = styled.div`
    .form-select { border-color: #cbd5e1; }
`;

const SerializedItemRow = React.memo(({ item, itemDetails, index, remove, onQtyChange, onEditVariant, onOpenB2B, policies, setFieldValue, duplicateSerials, onBulkApply, values, isGlobalExpanded, ...props }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Sync with global toggle
  React.useEffect(() => {
    setIsExpanded(isGlobalExpanded);
  }, [isGlobalExpanded]);
  const hasMissingSerials = useMemo(() => item.serializedItems?.some(s => !s.serialNumber?.trim()), [item.serializedItems]);
  const hasDuplicateSerials = useMemo(() => item.serializedItems?.some(s => {
    const sn = s.serialNumber?.trim();
    return sn && duplicateSerials[sn] > 1;
  }), [item.serializedItems, duplicateSerials]);

  const [tagModal, setTagModal] = useState({ show: false, itemIndex: null, unitIdx: null });

  // 2. Define the opener
  const handleOpenTagModal = useCallback((itemIdx, uIdx) => {
    setTagModal({ show: true, itemIndex: itemIdx, unitIdx: uIdx });
  }, []);

  const [showGenerator, setShowGenerator] = useState(false);

  const handleBulkGenerate = useCallback((config) => {
    const { prefix, startNumber, quantity, padding } = config;
    const newItems = Array.from({ length: quantity }, (_, i) => {
      const num = (parseInt(startNumber) + i).toString().padStart(padding, '0');
      return {
        tempId: Date.now() + i,
        serialNumber: `${prefix}${num}`,
        unitCost: item.unitCost || 0,
        sellingPrice: item.sellingPrice || 0,
        condition: item.condition || "Brand New",
        warrantyPolicyId: item.warrantyPolicyId || null,
        variant_id: item.variant_id
      };
    });

    setFieldValue(`purchasedItems[${index}].serializedItems`, newItems);
    setFieldValue(`purchasedItems[${index}].purchaseQty`, quantity);
    setFieldValue(`purchasedItems[${index}].total_price`, quantity * (item.unitCost || 0));
    toast.success(`Generated ${quantity} serials successfully!`);
  }, [index, item.unitCost, item.sellingPrice, item.condition, item.warrantyPolicyId, item.variant_id, setFieldValue]);

  const generateSerial = React.useCallback((idx) => {
    const timestamp = (Date.now() + idx).toString().slice(-7);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const prefix = (itemDetails?.itemName || "ITM").substring(0, 3).toUpperCase();
    setFieldValue(`purchasedItems[${index}].serializedItems[${idx}].serialNumber`, `${prefix}${timestamp}${random}`);
  }, [index, itemDetails?.itemName, setFieldValue]);

  const handleQuickRandom = React.useCallback(() => {
    const prefix = (itemDetails?.itemName || "ITM").substring(0, 3).toUpperCase();
    const currentUnits = [...(item.serializedItems || [])];
    const updated = currentUnits.map((u, i) => {
      const timestamp = (Date.now() + i).toString().slice(-7);
      const rand = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      return { ...u, serialNumber: `${prefix}${timestamp}${rand}` };
    });
    setFieldValue(`purchasedItems[${index}].serializedItems`, updated);
    toast.success(`Randomized ${updated.length} serials!`);
  }, [index, itemDetails?.itemName, item.serializedItems, setFieldValue]);

  const handleKeyDown = (e, uIdx) => {
    if (e.key === "Enter") {
      e.preventDefault();
      let nextInput = document.getElementById(`serial-input-${index}-${uIdx + 1}`);
      if (!nextInput) {
        nextInput = document.getElementById(`serial-input-${index + 1}-0`);
      }
      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  const debouncedCostUpdate = React.useCallback(
    debounce((itemIndex, uIdx, val, currentUnits, setFieldValue) => {
      const numericValue = parseFloat(val) || 0;

      // 1. Calculate new total based on this change
      const updatedUnits = [...currentUnits];
      updatedUnits[uIdx] = { ...updatedUnits[uIdx], unitCost: numericValue };
      const newTotal = updatedUnits.reduce((sum, u) => sum + (parseFloat(u.unitCost) || 0), 0);

      // 2. Update the Parent Total (Heavy Operation)
      setFieldValue(`purchasedItems[${itemIndex}].total_price`, newTotal);
    }, 500),
    []
  );

  const handleUnitChange = React.useCallback((itemIndex, uIdx, field, value) => {
    const numericValue = value === "" ? "" : (field === 'serialNumber' || field === 'warrantyPolicyId' || field === 'refurb_tags' ? value : parseFloat(value) || 0);
    setFieldValue(`purchasedItems[${itemIndex}].serializedItems[${uIdx}].${field}`, numericValue);

    if (field === 'unitCost') {
      debouncedCostUpdate(itemIndex, uIdx, value, values.purchasedItems, setFieldValue);
    }
  }, [setFieldValue, values.purchasedItems]);

  return (
    <>
      <style>
        {
          `.bulk-input-field::placeholder {
    font-size: 0.75rem !important;
    font-weight: 800;
    opacity: 0.9;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #000000;
}`
        }
      </style>
      {/* --- MAIN PARENT ROW (Bulk Controls) --- */}
      <tr className="pointer-cursor bg-white position-relative" style={{ borderBottom: '1px solid #e2e8f0' }}>
        <td className="ps-4 py-2 align-top" style={{ width: '350px' }}>
          <div className="d-flex align-items-start gap-3">
            {/* HYPER-VISIBILITY STATUS BAR (8px) */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '8px',
              height: '100%',
              backgroundColor: hasMissingSerials ? '#dc3545' : hasDuplicateSerials ? '#ffc107' : '#28a745',
              boxShadow: '2px 0 5px rgba(0,0,0,0.1)'
            }} />

            <div
              onClick={() => setIsExpanded(!isExpanded)}
              className="d-flex align-items-center justify-content-center fw-900 text-white shadow-sm flex-shrink-0 ms-2"
              style={{
                width: '28px',
                height: '28px',
                fontSize: '0.85rem',
                backgroundColor: '#001c3a',
                borderRadius: '4px',
                marginTop: '2px',
                cursor: 'pointer',
              }}
            >
              {String(index + 1).padStart(2, '0')}
            </div>

            {/* 2. INFORMATION STACK */}
            <div className="d-flex flex-column gap-0 overflow-hidden">
              {/* Main Title Row */}
              <div className="fw-black text-dark text-truncate" style={{ fontSize: '1rem', lineHeight: '1.2', color: '#000000', letterSpacing: '-0.02em' }}>
                {item.itemName}
              </div>

              {/* Sub-text: Barcode */}
              <div className="text-secondary font-monospace mb-2 fw-black" style={{ fontSize: '0.8rem', letterSpacing: '0.05em' }}>
                <BiBarcode className="me-1" />{item.barcode || 'NO-BARCODE'}
              </div>

              {/* Action Row: Compact Tags & Buttons */}
              <div className="d-flex flex-wrap align-items-center gap-1">
                {/* Expansion Toggle: Deep Style */}
                <button
                  type="button"
                  onClick={() => setIsExpanded(!isExpanded)}
                  className={`btn btn-sm py-0 px-2 d-flex align-items-center gap-1 border-0 fw-900 transition-all ${isExpanded ? 'bg-dark text-white' : 'bg-primary text-white'
                    }`}
                  style={{ fontSize: '0.75rem', height: '22px', borderRadius: '4px' }}
                >
                  <BiChevronRight
                    style={{
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s'
                    }}
                  />
                  {isExpanded ? "HIDE UNITS" : `${item.serializedItems?.length || 0} UNITS`}
                </button>

                {/* Config Button: Accessible for ALL serialized items */}
                <button
                  type="button"
                  onClick={() => onEditVariant(index, itemDetails)}
                  className="btn btn-sm btn-outline-primary py-0 px-2 fw-800 d-flex align-items-center gap-1"
                  style={{ fontSize: '0.6rem', height: '18px', borderRadius: '4px' }}
                >
                  <FaWrench size={8} /> CONFIG
                </button>

                <button
                  type="button"
                  onClick={() => setShowGenerator(true)}
                  className="btn btn-sm btn-dark py-0 px-2 fw-800 d-flex align-items-center gap-1"
                  style={{ fontSize: '0.6rem', height: '18px', borderRadius: '4px', backgroundColor: '#001c3a' }}
                >
                  <BiBarcode size={10} /> BULK GEN
                </button>

                <button
                  type="button"
                  onClick={handleQuickRandom}
                  className="btn btn-sm btn-outline-success py-0 px-2 fw-800 d-flex align-items-center gap-1"
                  style={{ fontSize: '0.6rem', height: '18px', borderRadius: '4px' }}
                >
                  <BiGridAlt size={10} /> RND
                </button>

                {/* Variant Attributes: Minimalist Pills */}
                {item.variantAttributes?.map((v, i) => (
                  <span
                    key={i}
                    className="px-2 py-0 border border-dark text-dark fw-bold rounded-pill"
                    style={{ fontSize: '0.75rem', height: '20px', lineHeight: '18px', backgroundColor: '#f8fafc' }}
                  >
                    {v.value}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </td>

        <td style={{ width: '80px', minWidth: '80px' }}>
          <div className="d-flex align-items-center justify-content-center">
            <FormControl
              type="number"
              value={item.purchaseQty}
              onChange={(e) => onQtyChange(e.target.value, index, item)}
              className="text-center fw-bold border-primary shadow-none"
              style={{ height: '35px', fontSize: '0.9rem', width: '60px' }}
              min="1"
            />
          </div>
        </td>

        <td style={{ width: '120px', minWidth: '120px' }}>
          <div className="text-muted x-small text-center fw-800 opacity-75 fst-italic">SEE UNITS</div>
        </td>

        <td style={{ width: '120px', minWidth: '120px' }} className="px-1">
          <div className="d-flex align-items-center position-relative">
            <FormControl
              type="number"
              placeholder="COST"
              value={item.unitCost || ""}
              onChange={(e) => setFieldValue(`purchasedItems[${index}].unitCost`, parseFloat(e.target.value) || 0)}
              className="bulk-input-field text-end fw-bold pe-5 shadow-none"
              style={{
                fontSize: '0.94rem',
                height: '35px',
                backgroundColor: '#ffffff',
                border: '1.5px solid #000000',
                borderRadius: '4px',
                color: '#000000',
                paddingRight: '30px'
              }}
            />
            <button
              type="button"
              onClick={() => onBulkApply(index, 'unitCost')}
              className="position-absolute end-0 d-flex align-items-center justify-content-center"
              style={{
                width: '24px',
                height: '32px',
                backgroundColor: '#001c3a',
                color: 'white',
                border: 'none',
                borderRadius: '0 4px 4px 0',
                top: '1.5px',
                right: '1.5px',
                zIndex: 5,
                cursor: 'pointer'
              }}
              title="Apply cost to all units"
            >
              <BiCheckDouble size={14} />
            </button>
          </div>
        </td>

        <td style={{ width: '120px', minWidth: '120px' }} className="px-1">
          <div className="d-flex align-items-center position-relative">
            <FormControl
              type="number"
              placeholder="PRICE"
              value={item.sellingPrice || ""}
              onChange={(e) => setFieldValue(`purchasedItems[${index}].sellingPrice`, parseFloat(e.target.value) || 0)}
              className="bulk-input-field text-end fw-bold pe-5 shadow-none"
              style={{
                fontSize: '0.94rem',
                height: '35px',
                backgroundColor: '#ffffff',
                border: '1.5px solid #000000',
                borderRadius: '4px',
                color: '#000000',
                paddingRight: '30px'
              }}
            />
            <button
              type="button"
              onClick={() => onBulkApply(index, 'sellingPrice')}
              className="position-absolute end-0 d-flex align-items-center justify-content-center"
              style={{
                width: '24px',
                height: '32px',
                backgroundColor: '#001c3a',
                color: 'white',
                border: 'none',
                borderRadius: '0 4px 4px 0',
                top: '1.5px',
                right: '1.5px',
                zIndex: 5,
                cursor: 'pointer'
              }}
              title="Apply price to all units"
            >
              <BiCheckDouble size={14} />
            </button>
          </div>
        </td>

        <td style={{ width: '110px', minWidth: '110px' }} className="px-1">
          <BootstrapForm.Select
            size="sm"
            value={item.priceVisibility || "ALL"}
            onChange={(e) => {
              setFieldValue(`purchasedItems[${index}].priceVisibility`, e.target.value);
            }}
            className="shadow-none fw-bold"
            style={{
              fontSize: '0.75rem',
              height: '35px',
              border: '1.5px solid #000000',
              backgroundColor: '#f8f9fa',
              color: '#000000'
            }}
          >
            <option value="ALL">🌐 PUBLIC</option>
            <option value="LOGGED_IN">🔒 LOGGED IN</option>
            <option value="B2B_ONLY">💼 B2B ONLY</option>
            <option value="STORE_ONLY">🏪 STORE ONLY</option>
          </BootstrapForm.Select>
          <Button variant="outline-primary" size="sm" className="w-100 mt-1 x-small py-0" onClick={onOpenB2B}>
            B2B Pricing
          </Button>
        </td>

        <td className="text-end pe-4 align-middle" style={{ width: '140px', minWidth: '140px' }}>
          <div style={{
            fontWeight: '900',
            fontSize: '1.2rem',
            color: '#000000'
          }} className="d-flex flex-column align-items-end">
            <div
              className="text-dark fw-900 mb-1"
              style={{ fontWeight: '900', fontSize: '0.75rem', letterSpacing: '0.05em' }}
            >
              LINE TOTAL
            </div>

            <div
              className="fw-900 font-monospace"
              style={{
                fontWeight: '900',
                fontSize: '1.2rem',
                color: '#000000'
              }}
            >
              {formatCurrency(item.total_price)}
            </div>

            <div
              style={{ fontWeight: '800', fontSize: '0.8rem', color: '#000000', marginTop: '2px' }}
            >
              {item.purchaseQty} × {formatCurrency(item.unitCost)}
            </div>
          </div>
        </td>

        <td style={{ width: '50px' }} className="text-center">
          <Button variant="link" size="sm" className="text-danger opacity-50 p-0" onClick={() => remove(index)}>
            <BiTrash size={18} />
          </Button>
        </td>
      </tr>


      {/* --- CHILD UNITS (THE MAT) --- */}
      {isExpanded && (
        <tr>
          <td colSpan={100} className="p-0 border-0">
            <SerializedItemsGrid
              item={item}
              itemIndex={index}
              duplicateSerials={duplicateSerials}
              setFieldValue={setFieldValue}
              policies={policies}
              onQtyChange={onQtyChange}
              openTagModal={handleOpenTagModal}
              generateSerial={generateSerial}
            />
          </td>
        </tr>
      )}

      <BulkSerialGenerator
        show={showGenerator}
        onHide={() => setShowGenerator(false)}
        onGenerate={handleBulkGenerate}
        itemName={item.itemName}
      />




      <Modal show={tagModal.show} onHide={() => setTagModal({ ...tagModal, show: false })} centered size="sm">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="h6 fw-bold">Unit Repair Logs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label className="x-small fw-bold text-muted uppercase mb-2">Select Known Issues/Repairs</label>
          <RefurbTagInput
            tags={values.purchasedItems[tagModal.itemIndex]?.serializedItems[tagModal.unitIdx]?.refurb_tags || []}
            setTags={(newTags) => handleUnitChange(tagModal.itemIndex, tagModal.unitIdx, 'refurb_tags', newTags)}
          />
        </Modal.Body>
      </Modal>
    </>
  );
});

export default SerializedItemRow;
